// ** react imports
import { useState, useMemo } from 'react';

// ** api methods for quiz response management
import {
  useUpdateSectionAnswerByAdminMutation,
  useGetQuizSectionQuery,
} from '../../../redux/slices/quizResponse/slice';

export const useUpdateSectionAnswerByAdmin = () => {
  try {
    // api call mutation
    const [
      updateSectionAnswerByAdmin,
      {
        isLoading: isUpdateSectionAnswerByAdminLoading,
        isError: isUpdateSectionAnswerByAdminError,
        error: updateSectionAnswerByAdminError,
        isSuccess: isUpdateSectionAnswerByAdminSuccess,
      },
    ] = useUpdateSectionAnswerByAdminMutation();

    // handlers
    const handleUpdateSectionAnswerByAdmin = async (
      sessionId,
      sectionNumber,
      data
    ) => {
      // Validate sessionId format
      if (!sessionId) {
        throw new Error('Session ID is required');
      }

      const prefix = 'session-';
      const isValidSessionId =
        sessionId.slice && sessionId.slice(prefix.length).length === 32;
      if (
        !sessionId.startsWith ||
        !sessionId.startsWith(prefix) ||
        !isValidSessionId
      ) {
        throw new Error('Invalid session ID');
      }

      // Validate sectionNumber
      if (!Number.isInteger(sectionNumber) || sectionNumber < 1) {
        throw new Error('Valid section number required');
      }

      // Validate answers array
      if (!Array.isArray(data.answers)) {
        throw new Error('Answers must be an array');
      }

      // Validate each answer
      for (const answer of data.answers) {
        if (!answer.questionId) {
          throw new Error('Question ID required for each answer');
        }
        if (answer.answer === undefined || answer.answer === null) {
          throw new Error('Answer value required for each question');
        }
      }

      // Validate reason if provided
      if (data.reason && typeof data.reason !== 'string') {
        throw new Error('Reason must be a string');
      }

      return await updateSectionAnswerByAdmin({
        sessionId,
        sectionNumber,
        data,
      });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isUpdateSectionAnswerByAdminLoading,
        isUpdateSectionAnswerByAdminError,
        isUpdateSectionAnswerByAdminSuccess,
        updateSectionAnswerByAdminError,
        updateSectionAnswerByAdmin: handleUpdateSectionAnswerByAdmin,
      }),
      [
        isUpdateSectionAnswerByAdminLoading,
        isUpdateSectionAnswerByAdminError,
        isUpdateSectionAnswerByAdminSuccess,
        updateSectionAnswerByAdminError,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error updating section answer by admin:', error);
    throw error;
  }
};

export const useGetQuizSection = () => {
  try {
    // states
    const [skipApi, setSkipApi] = useState(true);
    const [params, setParams] = useState({ sessionId: '', sectionNumber: '' });

    // api call query
    const {
      data: getQuizSectionData,
      isLoading: isGetQuizSectionLoading,
      isError: isGetQuizSectionError,
      error: getQuizSectionError,
      refetch: refetchGetQuizSection,
    } = useGetQuizSectionQuery(params, { skip: skipApi });

    // handlers
    const handleGetQuizSection = async (sessionId, sectionNumber) => {
      // Validate sessionId format
      if (!sessionId) {
        throw new Error('Session ID is required');
      }

      const prefix = 'session-';
      const isValidSessionId =
        sessionId.slice && sessionId.slice(prefix.length).length === 32;
      if (
        !sessionId.startsWith ||
        !sessionId.startsWith(prefix) ||
        !isValidSessionId
      ) {
        throw new Error('Invalid session ID');
      }

      // Validate sectionNumber
      if (!Number.isInteger(sectionNumber) || sectionNumber < 1) {
        throw new Error('Valid section number required');
      }

      setSkipApi(false);
      setParams({ sessionId, sectionNumber });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data: getQuizSectionData?.data,
        isGetQuizSectionLoading,
        isGetQuizSectionError,
        getQuizSectionError,
        refetchGetQuizSection,
        getQuizSection: handleGetQuizSection,
      }),
      [
        getQuizSectionData,
        isGetQuizSectionLoading,
        isGetQuizSectionError,
        getQuizSectionError,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching quiz section:', error);
    throw error;
  }
};
